import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import userContextSlice from '../../../../common/slices/userContextSlice';
import { handleOrgChange } from '../../../../routing/utils/LoginUtils';
import { isCurrentMemberOrgAdmin, isCurrentUserStaffOrSuperAdmin } from '../../../../common/selectors/auth';

import { employeeApi } from '../api/employeeApi';
import { PatchedPatchEmployee } from '../../../../store/generated/portal/employee/employees';

import { ControlContainer, ControlContainerRow, PageContent, PageHeader, PageSection } from 'common/styles/page';
import { StyledCancelButton, StyledSaveButton } from 'common/components/button/StyledButton';
import { useToasts } from 'common/toast';
import { Loader } from 'semantic-ui-react';
import Tabs from '../../../../common/components/Tabs';

import MemberCategoriesTab from './MemberEditCategoriesTab';
import MemberEditRecordsTab from './MemberEditRecordsTab';
import MemberEditProfileTab from './MemberEditProfileTab';
import { END_OF_SERVICE_REASONS, EndOfServiceReason } from '../utils/endOfServiceReasons';

import { ValidateEmail } from 'common/utils/EmailUtils';

export interface EmployeeData {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  departure_date?: string | null;
  departure_reason?: string | null;

  status: number;
  survey_status: number;
  account_type: number;
  organization_id: number;
  email_verified: boolean;
  demographic_values: Record<string, any>;
  employee_record_values: Record<number, any>;
  created_at: string;
  updated_at: string;
  [key: string]: any; // Dynamic demographic fields
}

const isEndOfServiceReason = (value: unknown): value is EndOfServiceReason =>
  END_OF_SERVICE_REASONS.some(reason => reason.value === value);

const MemberEdit = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const [activeTab, setActiveTab] = useState('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateEmployee] = employeeApi.useApiPortalV1EmployeeEmployeesPartialUpdateMutation();

  const [formData, setFormData] = useState<PatchedPatchEmployee>({});
  const [emailError, setEmailError] = useState<string | null>(null);

  const dispatch = useDispatch();

  // Get auth state
  const isAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);

  // Check if user has permission to manage employees
  const canManageEmployees = isAdmin || isOrgAdmin;

  // Get field definitions from user context
  const demographicFields = useSelector(userContextSlice.selectors.getDemographicFields);
  const employeeRecordFields = useSelector(userContextSlice.selectors.getEmployeeRecordFields);
  const currentOrganization = useSelector(userContextSlice.selectors.getCurrentOrganization);

  const hasEmployeeRecords = useMemo(
    () => currentOrganization?.employeeRecordsEnabled || false,
    [currentOrganization]
  );

  // Ensure organization context is loaded
  useEffect(() => {
    if (currentOrganization && (!demographicFields || !employeeRecordFields)) {
      handleOrgChange(dispatch, currentOrganization);
    }
  }, [currentOrganization, demographicFields, employeeRecordFields, dispatch]);

  // Fetch employee data with all related fields
  const {
    data: employeeData,
    isLoading: isLoadingEmployee,
    error: employeeError,
    refetch: refetchEmployeeData,
  } = employeeApi.useApiPortalV1EmployeeEmployeesRetrieveQuery(
    {
      id: parseInt(memberId || '0', 10),
      pk: parseInt(memberId || '0', 10),
    },
    { skip: !memberId }
  );

  // Update form data when employee data is loaded
  useEffect(() => {
    if (employeeData) {
      const data = employeeData as EmployeeData;
      const formDataUpdate: PatchedPatchEmployee = {
        first_name: data.first_name ?? undefined,
        last_name: data.last_name ?? undefined,
        email: data.email ?? undefined,
        departure_date: data.departure_date ?? undefined,
        departure_reason: isEndOfServiceReason(data.departure_reason)
          ? data.departure_reason
          : undefined,
        team_external_id: data.team_external_id ?? undefined,

        account_type:
          typeof data.account_type === 'number'
            ? data.account_type
            : data.account_type
              ? parseInt(data.account_type)
              : undefined,
        status:
          typeof data.status === 'number'
            ? data.status
            : data.status
              ? parseInt(data.status)
              : undefined,
        survey_status:
          typeof data.survey_status === 'number'
            ? data.survey_status
            : data.survey_status
              ? parseInt(data.survey_status)
              : undefined,
      };

      // Add demographic values to form data using field definitions from context
      if (demographicFields) {
        Object.entries(data.demographic_values).forEach(([fieldName, value]) => {
          const field = demographicFields[fieldName];
          if (field) {
            (formDataUpdate as any)[fieldName] = value;
          }
        });
      }

      // Add employee record values to form data using field definitions from context
      if (employeeRecordFields) {
        Object.entries(data.employee_record_values).forEach(([fieldId, value]) => {
          // Find the field definition by ID
          const field = Object.values(employeeRecordFields).find(f => f.id === parseInt(fieldId));
          if (field) {
            (formDataUpdate as any)[field.name] = value;
          }
        });
      }
      setFormData(formDataUpdate);
    }
  }, [employeeData, demographicFields, employeeRecordFields]);

  // Handle unauthorized access
  if (!canManageEmployees) {
    return <div>You do not have permission to manage members.</div>;
  }

  // Handle loading state
  if (isLoadingEmployee) {
    return (
      <div className="text-center">
        <Loader active inline="centered" />
      </div>
    );
  }

  // Handle error state
  if (employeeError) {
    return (
      <div className="text-center">
        <p>Error loading member data. Please try again later.</p>
      </div>
    );
  }

  // Handle not found state
  if (!employeeData) {
    return (
      <div className="text-center">
        <p>Member not found.</p>
      </div>
    );
  }

  const typedEmployeeData = employeeData as EmployeeData;

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle employee records input changes
  const handleEmployeeRecordsInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
      | { target: { name: string; value: any } }
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle number input changes
  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? undefined : Number(value),
    }));
  };

  // Handle dropdown changes
  const handleDropdownChange = (e: React.SyntheticEvent, data: any) => {
    const { name, value } = data;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  /*   const isFormValid = () => {
      if (!formData.first_name || !formData.last_name || !formData.account_type || !formData.email) {
        return false;
      }
      if (!ValidateEmail(formData.email)) {
        return false;
      }
      return true;
    };
   */
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const email = formData.email?.trim() || '';

    if (!formData.first_name || !formData.last_name || !formData.account_type || !email) {
      addToast('Please fill out all required fields', {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: 5000,
        key: 'form-validation-error',
      });
      return;
    }

    if (!ValidateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      addToast('Please enter a valid email address', {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: 5000,
        key: 'email-validation-error',
      });
      return;
    }

    setEmailError(null);
    setIsSubmitting(true);


    try {
      // Build demographic_data object
      const demographic_data: Record<string, any> = {};
      const formDataAny = formData as Record<string, any>;
      if (demographicFields) {
        Object.values(demographicFields).forEach(field => {
          const fieldKey = field.name === 'role' ? 'demographic_role' : field.name;
          if (formDataAny[fieldKey] !== undefined) {
            demographic_data[field.name] = formDataAny[fieldKey];
          }
        });
      }

      // Build employee_record_data object (keys are field IDs)
      const employee_record_data: Record<number, any> = {};
      if (employeeRecordFields) {
        Object.values(employeeRecordFields).forEach(field => {
          if (formDataAny[field.name] !== undefined) {
            employee_record_data[field.id] = formDataAny[field.name];
          }
        });
      }

      // Build the PATCH payload
      const departureReason = formDataAny.departure_reason;
      const isArchivedMember = Number(formData.status ?? typedEmployeeData.status) === 99;
      const normalizedFormData: PatchedPatchEmployee = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        team_external_id: formData.team_external_id ?? undefined,
        account_type: formData.account_type,
        status: formData.status,
        survey_status: formData.survey_status,
        demographic_data: Object.keys(demographic_data).length > 0 ? demographic_data : undefined,
        employee_record_data:
          Object.keys(employee_record_data).length > 0 ? employee_record_data : undefined,
        email: formData.email || undefined,
        ...(isArchivedMember
          ? {
              departure_date: formDataAny.departure_date || null,
              departure_reason: isEndOfServiceReason(departureReason) ? departureReason : null,
            }
          : {}),
      };

      // Update the employee data
      const result = await updateEmployee({
        id: parseInt(memberId || '0', 10),
        pk: parseInt(memberId || '0', 10),
        patchedPatchEmployee: normalizedFormData,
      }).unwrap();

      if (result) {
        addToast('Member updated successfully', {
          appearance: 'success',
          autoDismiss: true,
          autoDismissTimeout: 2000,
          key: 'employee-update-success',
        });
        dispatch(employeeApi.util.invalidateTags([{ type: 'Employee', id: 'LIST' }]));
        navigate(-1);
      } else {
        throw new Error('Failed to update member');
      }
    } catch (error) {
      console.error('Failed to update member:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update member';
      addToast(errorMessage, {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: 5000,
        key: 'employee-update-error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContent>
      <PageHeader
        title={typedEmployeeData?.first_name + ' ' + typedEmployeeData?.last_name}
        controls={
          <ControlContainerRow>
            <ControlContainer justify="left">
              <StyledCancelButton onClick={() => navigate(-1)} />
              <StyledSaveButton onClick={handleSubmit} />
            </ControlContainer>
          </ControlContainerRow>
        }
      />

      <PageSection>
        <form onSubmit={handleSubmit}>
          <Tabs
            borderbottom
            tabs={[
              {
                name: 'profile',
                label: 'Profile',
                content: (
                  <MemberEditProfileTab
                    formData={formData}
                    employeeData={typedEmployeeData}
                    onInputChange={handleInputChange}
                    onNumberInputChange={handleNumberInputChange}
                    onDropdownChange={handleDropdownChange}
                  />
                ),
              },
              {
                name: 'category-options',
                label: 'Demographics',
                content: (
                  <MemberCategoriesTab
                    formData={formData}
                    demographicFields={demographicFields ? Object.values(demographicFields) : []}
                    onDropdownChange={handleDropdownChange}
                  />
                ),
              },
              ...(hasEmployeeRecords
                ? [
                    {
                      name: 'employee-records',
                      label: 'Records',
                      content: (
                        <MemberEditRecordsTab
                          employee_id={typedEmployeeData.id}
                          employeeRecordFields={employeeRecordFields || {}}
                          onInputChange={handleEmployeeRecordsInputChange}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </form>
      </PageSection>
    </PageContent>
  );
};

export default MemberEdit;
